import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { axios } from '../../context/AuthContext';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';

/* ── Animated Counter ─────────────────────────────────── */
function Counter({ value, duration = 1200, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const [popped, setPopped] = useState(false);

  useEffect(() => {
    const end = parseFloat(value) || 0;
    if (end === 0) return;
    const start = Date.now();
    const tick = () => {
      const t = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(ease * end));
      if (t < 1) requestAnimationFrame(tick);
      else setPopped(true);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);

  return (
    <span style={{ display: 'inline-block', animation: popped ? 'none' : undefined }}>
      {prefix}{display}{suffix}
    </span>
  );
}

/* ── Chart Tooltip ─────────────────────────────────────── */
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: p.color, fontWeight: 700 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </div>
      ))}
    </div>
  );
};

/* ── Alert Card ────────────────────────────────────────── */
function AlertCard({ item, index }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), index * 80 + 300); }, [index]);
  return (
    <div style={{
      padding: '12px 16px',
      background: 'var(--accent-dim)',
      border: '1.5px solid var(--accent-border)',
      borderRadius: 'var(--r-md)',
      display: 'flex', alignItems: 'center', gap: 12,
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateX(-10px)',
      transition: 'all 0.35s ease',
      cursor: 'pointer',
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 'var(--r-md)', background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
        ⚠
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{item.ad}</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          Son net: <span style={{ color: 'var(--accent-light)', fontWeight: 700 }}>{item.son_net != null ? parseFloat(item.son_net).toFixed(1) : '-'}</span>
          {item.onceki_net != null && <> · Önceki: {parseFloat(item.onceki_net).toFixed(1)}</>}
        </div>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--accent-light)', fontWeight: 700, background: 'rgba(192,57,43,0.1)', padding: '3px 8px', borderRadius: 'var(--r-full)', flexShrink: 0 }}>
        ↓ Düşüş
      </div>
    </div>
  );
}

/* ── Stat Card ─────────────────────────────────────────── */
function StatCard({ label, value, icon, color, path, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { setTimeout(() => setVisible(true), delay); }, [delay]);
  return (
    <div
      className="stat-card"
      style={{
        '--stat-color': color,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.4s ease, transform 0.4s ease`,
      }}
      onClick={() => path && navigate(path)}
    >
      <div className="stat-icon">{icon}</div>
      <div className="stat-value">
        <Counter value={value} duration={1000} />
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/* ── Main Dashboard ────────────────────────────────────── */
export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [analiz, setAnaliz] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get('/admin/dashboard'),
      axios.get('/admin/sistem-analiz'),
    ]).then(([d, a]) => {
      if (d.data.success) setData(d.data.data);
      if (a.data.success) setAnaliz(a.data.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="page-loader">
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ width: 36, height: 36, margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Veriler yükleniyor...</p>
      </div>
    </div>
  );

  const ozet = data?.ozet || {};
  const denemeler = (data?.deneme_ortalama || []).reverse();
  const dusenler = data?.dusen_ogrenciler || [];

  const chartData = denemeler.map(d => ({
    name: d.ad?.split(' ').slice(0, 2).join(' ') || '-',
    'Ort. Net': parseFloat(d.ort_net || 0).toFixed(1),
    Katılım: d.katilimci || 0,
  }));

  const STATS = [
    { label: 'Öğrenci',        value: ozet.ogrenci_sayisi  || 0, icon: '◉', color: '#3AACCF', path: '/admin/ogrenciler', delay: 0 },
    { label: 'Öğretmen',       value: ozet.ogretmen_sayisi || 0, icon: '◎', color: '#5B8DD4', path: '/admin/ogretmenler', delay: 80 },
    { label: 'Veli',           value: ozet.veli_sayisi     || 0, icon: '◍', color: '#D4853A', path: '/admin/veliler', delay: 160 },
    { label: 'Bu Ay Devamsız', value: ozet.devamsizlik_bu_ay || 0, icon: '⚠', color: '#C0392B', path: '/admin/ogrenciler', delay: 240 },
    { label: 'Bekleyen Randevu', value: ozet.bekleyen_randevu || 0, icon: '▨', color: '#9B72CF', path: '/admin/randevular', delay: 320 },
    { label: 'Veli Görüşmesi', value: ozet.veli_gorusme_bu_ay || 0, icon: '◈', color: '#27AE60', path: '/admin/gorusmeler', delay: 400 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Welcome Banner */}
      <div className="welcome-banner animate-fadeInDown">
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--accent-light)', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Hoş Geldiniz
          </div>
          <h1 style={{ fontSize: '1.8rem', marginBottom: 6, color: 'var(--text-bright)' }}>
            {user?.ad} {user?.soyad}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            {new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        {dusenler.length > 0 && (
          <div style={{
            background: 'rgba(192,57,43,0.1)',
            border: '1px solid rgba(192,57,43,0.25)',
            borderRadius: 'var(--r-md)',
            padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: '1.1rem' }}>⚠</span>
            <span style={{ fontSize: '0.85rem', color: 'var(--accent-light)', fontWeight: 600 }}>
              {dusenler.length} öğrencide düşüş trendi tespit edildi
            </span>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-6 stagger">
        {STATS.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-2" style={{ gap: 20 }}>
        {/* Area Chart */}
        <div className="card animate-fadeInUp" style={{ animationDelay: '200ms' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: 3 }}>Deneme Ortalamaları</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Son 5 deneme trendi</p>
            </div>
            <span className="badge badge-info">{denemeler.length} deneme</span>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaNet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#C0392B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C0392B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="Ort. Net" stroke="#C0392B" strokeWidth={2} fill="url(#areaNet)" dot={{ fill: '#C0392B', strokeWidth: 0, r: 3 }} activeDot={{ r: 5, fill: '#E74C3C' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Henüz deneme verisi yok
            </div>
          )}
        </div>

        {/* Bar Chart — Katılım */}
        <div className="card animate-fadeInUp" style={{ animationDelay: '300ms' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: 3 }}>Deneme Katılımı</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Katılımcı sayısı</p>
            </div>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="Katılım" fill="#C0392B" fillOpacity={0.7} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Henüz veri yok
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-2" style={{ gap: 20 }}>
        {/* Düşüş uyarıları */}
        <div className="card animate-fadeInUp" style={{ animationDelay: '400ms' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: 3 }}>Düşüş Trendi</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Dikkat gerektiren öğrenciler</p>
            </div>
            {dusenler.length > 0 && (
              <span className="badge badge-danger">{dusenler.length} öğrenci</span>
            )}
          </div>
          {dusenler.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>✓</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--success)' }}>Tüm öğrenciler stabil</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dusenler.slice(0, 5).map((item, i) => (
                <AlertCard key={i} item={item} index={i} />
              ))}
              {dusenler.length > 5 && (
                <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => navigate('/admin/sistem-analiz')}>
                  +{dusenler.length - 5} daha görüntüle →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Etüt önerileri */}
        <div className="card animate-fadeInUp" style={{ animationDelay: '480ms' }}>
          <div style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 3 }}>Etüt Önerileri</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Sistem analizi önerileri</p>
          </div>
          {(!analiz?.etut_onerileri || analiz.etut_onerileri.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>◈</div>
              <div style={{ fontSize: '0.875rem' }}>Öneri bulunmuyor</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {analiz.etut_onerileri.slice(0, 5).map((item, i) => {
                const pct = Math.min((item.dusuk_ogrenci / (item.dusuk_ogrenci + 3)) * 100, 100);
                return (
                  <div key={i} style={{ padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.ders_adi}</span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--accent-light)', fontWeight: 700 }}>{item.dusuk_ogrenci} öğrenci</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress-bar" style={{ flex: 1, height: 5 }}>
                        <div className="progress-fill" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, var(--accent-muted), var(--accent-light))' }} />
                      </div>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0 }}>Ort: {parseFloat(item.ort_net || 0).toFixed(1)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
